import { Select } from '@servicecompressionllc/sc-design-system';
import { Fragment } from 'react';
import type { BuilderCondition } from '../../../lib/types.js';
import { conditionOperators, OPS } from '../conditionFields.js';
import { ConditionValue } from './ConditionValue.js';
import { type PickerVariable } from '../definitionEdit.js';
import { FieldChooser } from './FieldChooser.js';

export const ConditionRows = ({
  conditions,
  vars,
  match,
  onMatchChange,
  onChange,
}: {
  conditions: BuilderCondition[];
  vars: PickerVariable[];
  /** How multiple conditions combine — 'all' = and, 'any' = or. The
   * connector between rows IS this control; an invisible semantic is a bug. */
  match: 'all' | 'any';
  onMatchChange: (m: 'all' | 'any') => void;
  onChange: (next: BuilderCondition[]) => void;
}) => (
  <div className="grid min-w-0 gap-2">
    {conditions.map((c, i) => (
      <Fragment key={i}>
        {i > 0 && (
          <div className="w-24">
            <Select
              aria-label="Combine conditions with"
              searchable={false}
              options={[
                { value: 'all', label: 'and' },
                { value: 'any', label: 'or' },
              ]}
              value={[match]}
              onChange={(v) => onMatchChange((v[0] ?? 'all') as 'all' | 'any')}
            />
          </div>
        )}
        {/* ONE row: field · op · value, delete pinned at the end — the ✕ must
            visually belong to the line it removes. */}
        <div className="flex min-w-0 items-center gap-2">
          <div className="min-w-0 flex-1 basis-52">
            <FieldChooser
              value={c.path}
              vars={vars}
              onChange={(path) =>
                onChange(
                  conditions.map((x, j) =>
                    j === i
                      ? {
                          ...x,
                          path,
                          op: conditionOperators(vars.find((v) => v.path === path)?.kind).includes(
                            x.op,
                          )
                            ? x.op
                            : conditionOperators(vars.find((v) => v.path === path)?.kind)[0]!,
                          value: '',
                        }
                      : x,
                  ),
                )
              }
            />
          </div>
          <div className="w-28 flex-none">
            <Select
              aria-label="Operator"
              searchable={false}
              options={OPS.filter(
                (option) =>
                  conditionOperators(vars.find((v) => v.path === c.path)?.kind).includes(
                    option.value as BuilderCondition['op'],
                  ) || option.value === c.op,
              )}
              value={[c.op]}
              onChange={(v) =>
                onChange(
                  conditions.map((x, j) =>
                    j === i ? { ...x, op: (v[0] ?? 'eq') as BuilderCondition['op'] } : x,
                  ),
                )
              }
            />
          </div>
          {c.op !== 'exists' && (
            <div className="min-w-0 flex-1 basis-32">
              <ConditionValue
                key={`${c.path}:${c.op}`}
                value={String(c.value ?? '')}
                kind={vars.find((v) => v.path === c.path)?.kind}
                values={
                  c.op === 'eq' || c.op === 'neq'
                    ? vars.find((v) => v.path === c.path)?.values
                    : undefined
                }
                onChange={(value) =>
                  onChange(conditions.map((x, j) => (j === i ? { ...x, value } : x)))
                }
              />
            </div>
          )}
          <button
            type="button"
            aria-label="Remove condition"
            title="Remove this condition"
            className="ml-auto flex-none text-xs text-ink-subtle hover:text-danger"
            onClick={() => onChange(conditions.filter((_, j) => j !== i))}
          >
            ✕
          </button>
        </div>
      </Fragment>
    ))}
    <button
      type="button"
      className="mt-1 justify-self-start text-xs text-ink-muted hover:text-ink"
      onClick={() => onChange([...conditions, { path: '', op: 'eq', value: '' }])}
    >
      + Add condition
    </button>
  </div>
);
